import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  Divider,
  IconButton,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApiGet } from '../../utils/httpClientTs';
import {
  getMarketplaceCategories,
  getMarketplaceSubcategories,
  createMarketplaceItem,
  uploadMarketplaceImage,
  uploadMarketplaceDocument,
  createMarketplacePayment,
} from '../../endpoints/marketplace';
import { getPostalInfo } from '../../endpoints/lookupsTs';
import { getMyPermissions } from '../../endpoints/usersTs';
import AlertItem from '../../Components/AlertItem';
import { permissionNames } from '../../utils/variables';
import { MarketplaceImageUpload } from '../../Components/Marketplace';
import ChapterSelector from '../../Components/ChapterSelector';
import CountrySelector from '../../Components/CountrySelector';
import StateSelector from '../../Components/StateSelector';
import { emailValidator, phoneNumberValidator } from '../../utils/validators';

interface MarketplaceFormData {
  name: string;
  description: string;
  price: string;
  category_id: string;
  subcategory_id: string;
  tags: string[];
  order_instructions: string;
  shipping_details: string;
  link_url: string;
  contact_name: string;
  show_contact_name: boolean;
  contact_email: string;
  show_contact_email: boolean;
  contact_phone: string;
  show_contact_phone: boolean;
  contact_address_1: string;
  contact_address_2: string;
  contact_city: string;
  contact_state: string;
  contact_state_id: string;
  contact_postal_code: string;
  contact_country: string;
  contact_country_id: string;
  show_contact_address: boolean;
  local_chapter_id: string;
  expiration_date: string;
}

interface FormErrors {
  contact_email?: string;
  contact_phone?: string;
  contact_country_id?: string;
  contact_state_id?: string;
  contact_postal_code?: string;
  expiration_date?: string;
}

const MarketplaceCreate: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<MarketplaceFormData>({
    name: '',
    description: '',
    price: '',
    category_id: '',
    subcategory_id: '',
    tags: [],
    order_instructions: '',
    shipping_details: '',
    link_url: '',
    contact_name: '',
    show_contact_name: true,
    contact_email: '',
    show_contact_email: true,
    contact_phone: '',
    show_contact_phone: true,
    contact_address_1: '',
    contact_address_2: '',
    contact_city: '',
    contact_state: '',
    contact_state_id: '',
    contact_postal_code: '',
    contact_country: '',
    contact_country_id: '',
    show_contact_address: false,
    local_chapter_id: '1',
    expiration_date: '',
  });
  const [tagInput, setTagInput] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [postalCodeData, setPostalCodeData] = useState<any>(null);
  const [imageMetadata, setImageMetadata] = useState<
    Array<{
      file: File;
      isPrimary: boolean;
      displayOrder: number;
    }>
  >([]);
  const [documentMetadata, setDocumentMetadata] = useState<
    Array<{
      file: File;
      displayName: string;
    }>
  >([]);

  // Fetch categories and subcategories
  const { data: categories } = useApiGet(
    ['marketplace-categories'],
    getMarketplaceCategories
  );

  const { data: allSubcategories = [] } = useApiGet(
    ['marketplace-subcategories'],
    getMarketplaceSubcategories
  );

  const { data: myPermissions, isLoading: permsLoading } = useApiGet(
    ['me-permissions', 'international'],
    () => getMyPermissions({ international: '1' })
  );
  const canModerate = Boolean(
    myPermissions?.includes(permissionNames.MarketplaceEdit)
  );

  // Postal code lookup effect
  useEffect(() => {
    if (formData.contact_postal_code && formData.contact_country_id) {
      const lookupPostalCode = async () => {
        try {
          const response = await getPostalInfo(
            formData.contact_postal_code,
            formData.contact_country_id
          );
          setPostalCodeData(response);
        } catch (error) {
          console.error('Error looking up postal code:', error);
          setPostalCodeData(null);
        }
      };

      const timeoutId = setTimeout(lookupPostalCode, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setPostalCodeData(null);
    }
  }, [formData.contact_postal_code, formData.contact_country_id]);

  // Update city and state when postal code data is received
  useEffect(() => {
    if (postalCodeData) {
      if (postalCodeData.city) {
        setFormData((prev) => ({
          ...prev,
          contact_city: postalCodeData.city,
        }));
      }
      if (postalCodeData.state_id) {
        setFormData((prev) => ({
          ...prev,
          contact_state_id: postalCodeData.state_id,
          contact_state: postalCodeData.state,
        }));
      }
    }
  }, [postalCodeData]);

  const steps = [
    'Item Details',
    'Images & Documents',
    'Contact Information',
    'Review & Payment',
  ];

  const handleInputChange = (field: keyof MarketplaceFormData, value: any) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Clear subcategory when category changes
      if (field === 'category_id') {
        newData.subcategory_id = '';
      }

      return newData;
    });

    // Clear validation errors when user starts typing
    if (formErrors[field as keyof FormErrors]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleCountryChange = (country: any) => {
    setFormData((prev) => ({
      ...prev,
      contact_country_id: country?.id || '',
      contact_country: country?.name || '',
      contact_state_id: '', // Reset state when country changes
      contact_state: '',
      contact_city: '', // Reset city when country changes
    }));

    // Clear validation errors
    setFormErrors((prev) => ({
      ...prev,
      contact_country_id: undefined,
      contact_state_id: undefined,
    }));
  };

  const handleStateChange = (state: any) => {
    setFormData((prev) => ({
      ...prev,
      contact_state_id: state?.id || '',
      contact_state: state?.name || '',
    }));

    // Clear validation errors
    setFormErrors((prev) => ({
      ...prev,
      contact_state_id: undefined,
    }));
  };

  // Filter subcategories by selected category
  const subcategories = allSubcategories.filter(
    (sub) => sub.category_id === formData.category_id
  );

  // Check if the selected category has subcategories
  const hasSubcategories = subcategories && subcategories.length > 0;

  const handleChapterSelect = (chapterId: string) => {
    setFormData((prev) => ({
      ...prev,
      local_chapter_id: chapterId,
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setDocuments((prev) => [...prev, ...files]);

    // Initialize metadata for new documents
    const newMetadata = files.map((file) => ({
      file,
      displayName: file.name, // Default to original filename
    }));
    setDocumentMetadata((prev) => [...prev, ...newMetadata]);
  };

  const handleRemoveDocument = (index: number) => {
    setDocuments((prev) => prev.filter((_, i) => i !== index) as File[]);
    setDocumentMetadata((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImageMetadataChange = (
    index: number,
    field: 'isPrimary' | 'displayOrder',
    value: any
  ) => {
    setImageMetadata((prev) => {
      const newMetadata = [...prev];
      newMetadata[index] = {
        ...newMetadata[index],
        [field]: value,
      };
      return newMetadata;
    });
  };

  const handleDocumentMetadataChange = (
    index: number,
    field: 'displayName',
    value: string
  ) => {
    setDocumentMetadata((prev) => {
      const newMetadata = [...prev];
      newMetadata[index] = {
        ...newMetadata[index],
        [field]: value,
      };
      return newMetadata;
    });
  };

  const handleImagesChange = (newImages: File[]) => {
    setImages(newImages);

    // Create metadata for all images, preserving existing metadata where possible
    const newMetadata = newImages.map((file, index) => {
      // Try to find existing metadata for this file
      const existingMeta = imageMetadata.find(
        (meta) => meta.file === file || meta.file.name === file.name
      );

      if (existingMeta) {
        // Preserve existing metadata, but update the file reference
        return {
          ...existingMeta,
          file, // Update file reference in case it changed
        };
      } else {
        // Create new metadata for new files
        const hasExistingPrimary = imageMetadata.some((meta) => meta.isPrimary);
        return {
          file,
          isPrimary: !hasExistingPrimary && index === 0, // First image is primary only if no primary exists
          displayOrder: index, // Start from 0
        };
      }
    });

    setImageMetadata(newMetadata);
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Validate that if we have images to upload, we have metadata for them
      if (images.length > 0 && images.length !== imageMetadata.length) {
        throw new Error(
          `Image metadata mismatch. Found ${images.length} image(s) but ${imageMetadata.length} metadata entry/entries. Please refresh and try again.`
        );
      }

      // Validate that if we have documents to upload, we have metadata for them
      if (
        documents.length > 0 &&
        documents.length !== documentMetadata.length
      ) {
        throw new Error(
          'Document metadata mismatch. Please refresh and try again.'
        );
      }

      // Upload images first to get their IDs with validation
      const uploadedImages: Array<{
        id: string;
        is_primary: boolean;
        display_order: number;
      }> = [];
      const imageUploadErrors: string[] = [];

      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        const metadata = imageMetadata[i];
        try {
          const response = await uploadMarketplaceImage(image);

          // Validate response has the required ID
          if (!response?.data?.id) {
            imageUploadErrors.push(
              `Failed to upload image "${image.name}": No image ID returned`
            );
            continue;
          }

          // Validate metadata exists
          if (!metadata) {
            imageUploadErrors.push(
              `Missing metadata for image "${image.name}"`
            );
            continue;
          }

          uploadedImages.push({
            id: response.data.id,
            is_primary: metadata.isPrimary || false,
            display_order: metadata.displayOrder ?? i,
          });
        } catch (error: any) {
          const errorMessage =
            error?.message || error?.response?.data?.message || 'Unknown error';
          imageUploadErrors.push(
            `Failed to upload image "${image.name}": ${errorMessage}`
          );
        }
      }

      // If any image uploads failed, throw error
      if (imageUploadErrors.length > 0) {
        throw new Error(
          `Image upload failed:\n${imageUploadErrors.join('\n')}`
        );
      }

      // Validate that all images we tried to upload have IDs
      if (images.length > 0 && uploadedImages.length !== images.length) {
        throw new Error(
          `Image upload incomplete. Expected ${images.length} image(s), but only ${uploadedImages.length} uploaded successfully.`
        );
      }

      // Upload documents with validation
      const uploadedDocuments: Array<{ id: string; name: string }> = [];
      const documentUploadErrors: string[] = [];

      for (let i = 0; i < documents.length; i++) {
        const document = documents[i];
        const metadata = documentMetadata[i];
        try {
          const response = await uploadMarketplaceDocument(document);

          // Validate response has the required ID
          if (!response?.data?.id) {
            documentUploadErrors.push(
              `Failed to upload document "${document.name}": No document ID returned`
            );
            continue;
          }

          // Validate metadata exists
          if (!metadata) {
            documentUploadErrors.push(
              `Missing metadata for document "${document.name}"`
            );
            continue;
          }

          uploadedDocuments.push({
            id: response.data.id,
            name: metadata.displayName || document.name,
          });
        } catch (error: any) {
          const errorMessage =
            error?.message || error?.response?.data?.message || 'Unknown error';
          documentUploadErrors.push(
            `Failed to upload document "${document.name}": ${errorMessage}`
          );
        }
      }

      // If any document uploads failed, throw error
      if (documentUploadErrors.length > 0) {
        throw new Error(
          `Document upload failed:\n${documentUploadErrors.join('\n')}`
        );
      }

      // Validate that all documents we tried to upload have IDs
      if (
        documents.length > 0 &&
        uploadedDocuments.length !== documents.length
      ) {
        throw new Error(
          `Document upload incomplete. Expected ${documents.length} document(s), but only ${uploadedDocuments.length} uploaded successfully.`
        );
      }

      // Helper function to convert empty strings to null
      const emptyStringToNull = (
        value: string | null | undefined
      ): string | null => {
        return value === '' ? null : value || null;
      };

      // Create the marketplace item with image and document references
      const itemData = {
        ...formData,
        subcategory_id: emptyStringToNull(formData.subcategory_id),
        order_instructions: emptyStringToNull(formData.order_instructions),
        shipping_details: emptyStringToNull(formData.shipping_details),
        link_url: formData.link_url.trim(),
        contact_address_1: emptyStringToNull(formData.contact_address_1),
        contact_address_2: emptyStringToNull(formData.contact_address_2),
        contact_city: emptyStringToNull(formData.contact_city),
        contact_state_id: emptyStringToNull(formData.contact_state_id),
        contact_postal_code: emptyStringToNull(formData.contact_postal_code),
        contact_country_id: emptyStringToNull(formData.contact_country_id),
        local_chapter_id: emptyStringToNull(formData.local_chapter_id),
        images: uploadedImages,
        document: uploadedDocuments.length > 0 ? uploadedDocuments[0] : null,
      };

      // Final validation: Ensure all uploaded image IDs are in the request
      const imageIdsInRequest = itemData.images.map((img) => img.id);
      const uploadedImageIds = uploadedImages.map((img) => img.id);
      const missingImageIds = uploadedImageIds.filter(
        (id) => !imageIdsInRequest.includes(id)
      );

      if (missingImageIds.length > 0) {
        throw new Error(
          `Validation error: Uploaded image IDs [${missingImageIds.join(', ')}] are missing from the create request.`
        );
      }

      // Final validation: Ensure uploaded document ID is in the request if we uploaded documents
      if (uploadedDocuments.length > 0) {
        const uploadedDocumentId = uploadedDocuments[0].id;
        if (!itemData.document || itemData.document.id !== uploadedDocumentId) {
          throw new Error(
            `Validation error: Uploaded document ID "${uploadedDocumentId}" is missing from the create request.`
          );
        }
      }

      const createdItem = await createMarketplaceItem(itemData);

      // Process payment
      await createMarketplacePayment({
        item_id: createdItem.id,
        amount: 2500, // $25.00 in cents
        payment_method_id: 'pm_mock_payment_method', // Mock payment method
      });

      navigate('/marketplace', {
        state: {
          message:
            'Your ad has been created successfully! It will be reviewed by an admin before going live.',
        },
      });
    } catch (error: any) {
      console.error('Error creating marketplace item:', error);

      // Extract the actual error message
      let errorMessage = 'Failed to create marketplace item. Please try again.';

      if (error?.message) {
        // This will catch all the throw new Error() calls above
        errorMessage = error.message;
      } else if (error?.response?.data?.message) {
        // API error response
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        // Alternative API error format
        errorMessage = error.response.data.error;
      } else if (typeof error === 'string') {
        // Error is already a string
        errorMessage = error;
      }

      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateStep = (step: number) => {
    const newErrors: FormErrors = {};

    if (step === 2) {
      // Validate email
      if (!formData.contact_email.trim()) {
        newErrors.contact_email = 'Email is required';
      } else {
        const emailError = emailValidator(formData.contact_email);
        if (emailError) {
          newErrors.contact_email = emailError;
        }
      }

      // Validate phone
      if (!formData.contact_phone.trim()) {
        newErrors.contact_phone = 'Phone is required';
      } else {
        const phoneError = phoneNumberValidator(formData.contact_phone);
        if (phoneError) {
          newErrors.contact_phone = phoneError;
        }
      }

      // Validate expiration date
      if (!formData.expiration_date) {
        newErrors.expiration_date = 'Expiration date is required';
      }

      // Address fields are now optional, but if provided, validate them
      if (formData.contact_country_id && !formData.contact_postal_code.trim()) {
        newErrors.contact_postal_code =
          'Postal code is required when country is selected';
      }

      // Validate state if country is US and address is being shown
      if (
        formData.contact_country_id === '1' &&
        formData.show_contact_address &&
        !formData.contact_state_id
      ) {
        newErrors.contact_state_id = 'State is required for US addresses';
      }
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isStepValid = (step: number) => {
    switch (step) {
      case 0:
        return (
          formData.name &&
          formData.description &&
          formData.price &&
          formData.category_id
        );
      case 1:
        return true; // Images and documents are optional
      case 2:
        return (
          formData.contact_name &&
          formData.contact_email &&
          formData.contact_phone &&
          formData.expiration_date &&
          !emailValidator(formData.contact_email) &&
          !phoneNumberValidator(formData.contact_phone)
        );
      case 3:
        return true; // Review step
      default:
        return false;
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Item Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange('description', e.target.value)
                }
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Price"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                placeholder="e.g., $50 or prices vary"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tags"
                placeholder="Add a tag and press Enter"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={handleAddTag}
                        variant="outlined"
                        size="small"
                      >
                        Add
                      </Button>
                    </InputAdornment>
                  ),
                }}
                helperText={
                  formData.tags.length > 0 ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      sx={{ mt: 1 }}
                    >
                      {formData.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag}
                          onDelete={() => handleRemoveTag(tag)}
                          size="small"
                        />
                      ))}
                    </Stack>
                  ) : (
                    'Press Enter or click Add to add tags'
                  )
                }
              />
            </Grid>
            <Grid item xs={12} md={hasSubcategories ? 6 : 12}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category_id}
                  onChange={(e) =>
                    handleInputChange('category_id', e.target.value)
                  }
                  label="Category"
                >
                  {categories?.map((category: any) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {hasSubcategories && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Subcategory</InputLabel>
                  <Select
                    value={formData.subcategory_id}
                    onChange={(e) =>
                      handleInputChange('subcategory_id', e.target.value)
                    }
                    label="Subcategory"
                  >
                    {subcategories?.map((subcategory: any) => (
                      <MenuItem key={subcategory.id} value={subcategory.id}>
                        {subcategory.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Order Instructions"
                value={formData.order_instructions}
                onChange={(e) =>
                  handleInputChange('order_instructions', e.target.value)
                }
                placeholder="How should buyers contact you or place orders?"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Shipping Information"
                value={formData.shipping_details}
                onChange={(e) =>
                  handleInputChange('shipping_details', e.target.value)
                }
                placeholder="Shipping options, costs, and policies"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="External Link (Optional)"
                value={formData.link_url}
                onChange={(e) => handleInputChange('link_url', e.target.value)}
                placeholder="https://example.com"
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MarketplaceImageUpload
                images={images}
                onImagesChange={handleImagesChange}
                imageMetadata={imageMetadata}
                onImageMetadataChange={handleImageMetadataChange}
                maxImages={10}
                maxSize={5}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Documents (Optional)
              </Typography>
              <Box sx={{ textAlign: 'center' }}>
                <input
                  id="document-upload"
                  type="file"
                  multiple
                  onChange={handleDocumentUpload}
                  style={{ display: 'none' }}
                />
                <label htmlFor="document-upload">
                  <Paper
                    sx={{
                      p: 2,
                      border: '2px dashed #ccc',
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: '#FF6B35',
                        backgroundColor: 'rgba(255, 107, 53, 0.04)',
                      },
                    }}
                  >
                    <CloudUploadIcon
                      sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Click to upload documents (order forms, etc.)
                    </Typography>
                  </Paper>
                </label>
              </Box>
              {documents.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {documents.map((document, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        p: 2,
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        backgroundColor: '#f9f9f9',
                      }}
                    >
                      <Box sx={{ flexGrow: 1, mr: 2 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Document Name"
                          value={
                            documentMetadata[index]?.displayName ||
                            document.name
                          }
                          onChange={(e) =>
                            handleDocumentMetadataChange(
                              index,
                              'displayName',
                              e.target.value
                            )
                          }
                          helperText={`Original: ${document.name}`}
                        />
                      </Box>
                      <IconButton
                        onClick={() => handleRemoveDocument(index)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Name"
                value={formData.contact_name}
                onChange={(e) =>
                  handleInputChange('contact_name', e.target.value)
                }
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.contact_email}
                onChange={(e) =>
                  handleInputChange('contact_email', e.target.value)
                }
                error={!!formErrors.contact_email}
                helperText={formErrors.contact_email}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.contact_phone}
                onChange={(e) =>
                  handleInputChange('contact_phone', e.target.value)
                }
                error={!!formErrors.contact_phone}
                helperText={formErrors.contact_phone}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expiration Date"
                type="date"
                value={formData.expiration_date}
                onChange={(e) =>
                  handleInputChange('expiration_date', e.target.value)
                }
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Contact Information Visibility
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose which contact information to display to shoppers
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <input
                    type="checkbox"
                    checked={formData.show_contact_name}
                    onChange={(e) =>
                      handleInputChange('show_contact_name', e.target.checked)
                    }
                  />
                  <Typography variant="body2">Show Contact Name</Typography>
                </Stack>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <input
                    type="checkbox"
                    checked={formData.show_contact_email}
                    onChange={(e) =>
                      handleInputChange('show_contact_email', e.target.checked)
                    }
                  />
                  <Typography variant="body2">Show Email</Typography>
                </Stack>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl component="fieldset">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <input
                    type="checkbox"
                    checked={formData.show_contact_phone}
                    onChange={(e) =>
                      handleInputChange('show_contact_phone', e.target.checked)
                    }
                  />
                  <Typography variant="body2">Show Phone</Typography>
                </Stack>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChapterSelector
                chapter={formData.local_chapter_id}
                handleChapterSelect={handleChapterSelect}
                spd={null}
                keyId="marketplace-create"
                label="Local Chapter"
                all={true}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CountrySelector
                value={formData.contact_country_id}
                handleChange={handleCountryChange}
                error={!!formErrors.contact_country_id}
                errorMessage={formErrors.contact_country_id}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.contact_postal_code}
                onChange={(e) =>
                  handleInputChange('contact_postal_code', e.target.value)
                }
                error={!!formErrors.contact_postal_code}
                helperText={formErrors.contact_postal_code}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StateSelector
                value={formData.contact_state_id}
                handleChange={handleStateChange}
                countryId={formData.contact_country_id}
                error={!!formErrors.contact_state_id}
                errorMessage={formErrors.contact_state_id}
                required={false}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.contact_city}
                onChange={(e) =>
                  handleInputChange('contact_city', e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <input
                    type="checkbox"
                    checked={formData.show_contact_address}
                    onChange={(e) =>
                      handleInputChange(
                        'show_contact_address',
                        e.target.checked
                      )
                    }
                  />
                  <Typography variant="body2">
                    Show Address Information
                  </Typography>
                </Stack>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={formData.contact_address_1}
                onChange={(e) =>
                  handleInputChange('contact_address_1', e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={formData.contact_address_2}
                onChange={(e) =>
                  handleInputChange('contact_address_2', e.target.value)
                }
              />
            </Grid>
            {/* Postal Code Lookup Results */}
            {postalCodeData && (
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <AlertItem
                    severity="success"
                    description={
                      <Typography variant="body2">
                        <strong>Location Found:</strong> {postalCodeData.city},{' '}
                        {postalCodeData.state}, {postalCodeData.country}
                      </Typography>
                    }
                    onClose={() => {}}
                    keepOpen={false}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Your Ad
            </Typography>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  {formData.name}
                </Typography>
                <Typography variant="h6" color="primary" gutterBottom>
                  {formData.price}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formData.description}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Category:{' '}
                  {
                    categories?.find((c: any) => c.id === formData.category_id)
                      ?.name
                  }
                </Typography>
                {formData.subcategory_id && (
                  <Typography variant="body2" color="text.secondary">
                    Subcategory:{' '}
                    {
                      subcategories?.find(
                        (s: any) => s.id === formData.subcategory_id
                      )?.name
                    }
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MoneyIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Ad Creation Fee: $25.00</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <AlertItem
                severity="info"
                description="Your ad will be reviewed by an admin before it becomes visible to the public. You will receive an email notification once it's approved."
                onClose={() => {}}
                keepOpen={true}
              />
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  if (permsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canModerate) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/marketplace')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Create Marketplace Ad
          </Typography>
        </Box>
        <AlertItem
          severity="error"
          description="You do not have permission to create marketplace ads. The marketplace:edit permission is required."
          onClose={() => {}}
          keepOpen={true}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/marketplace')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Create Marketplace Ad
        </Typography>
      </Box>

      {/* Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Box sx={{ mb: 3 }}>
          <AlertItem
            severity="error"
            description={
              <>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'bold', mb: 0.5 }}
                >
                  Error:
                </Typography>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    margin: 0,
                    fontFamily: 'inherit',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {error}
                </Typography>
              </>
            }
            onClose={() => setError(null)}
            keepOpen={true}
          />
        </Box>
      )}

      {/* Step Content */}
      <Card>
        <CardContent>{renderStepContent(activeStep)}</CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>
        <Box>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                backgroundColor: '#FF6B35',
                '&:hover': {
                  backgroundColor: '#E55A2B',
                },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} />
              ) : (
                'Create Ad & Pay $25'
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid(activeStep)}
              sx={{
                backgroundColor: '#FF6B35',
                '&:hover': {
                  backgroundColor: '#E55A2B',
                },
              }}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>

      {/* Validation Help Text */}
      {activeStep === 2 && !isStepValid(activeStep) && (
        <Box sx={{ mt: 2 }}>
          <AlertItem
            severity="info"
            description={
              <Typography variant="body2">
                Please complete all required fields to continue:
                {!formData.contact_name && ' • Contact Name'}
                {!formData.contact_email && ' • Email'}
                {!formData.contact_phone && ' • Phone'}
                {!formData.expiration_date && ' • Expiration Date'}
                {formData.contact_email &&
                  emailValidator(formData.contact_email) &&
                  ' • Valid Email'}
                {formData.contact_phone &&
                  phoneNumberValidator(formData.contact_phone) &&
                  ' • Valid Phone'}
              </Typography>
            }
            onClose={() => {}}
            keepOpen={true}
          />
        </Box>
      )}
    </Box>
  );
};

export default MarketplaceCreate;
